import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cx } from './cx';
import s from './Field.module.css';

/** Label, control, hint and error, wired together for screen readers. */
export function Field({ label, hint, error, children, className }: { label: ReactNode; hint?: ReactNode; error?: ReactNode; children: (id: string, describedBy: string | undefined) => ReactNode; className?: string }) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className={cx(s.field, className)}>
      <label className={s.label} htmlFor={id}>
        {label}
      </label>
      {children(id, describedBy)}
      {error ? (
        <span id={`${id}-error`} className={s.error}>
          {error}
        </span>
      ) : (
        hint && (
          <span id={`${id}-hint`} className={s.hint}>
            {hint}
          </span>
        )
      )}
    </div>
  );
}

export function Input({ invalid, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return <input className={cx(s.control, invalid && s.invalid, className)} aria-invalid={invalid || undefined} {...rest} />;
}

/** A multi-line text box with the same look as Input (messages, notes). */
export function Textarea({ invalid, className, rows = 4, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return <textarea className={cx(s.control, s.textarea, invalid && s.invalid, className)} rows={rows} aria-invalid={invalid || undefined} {...rest} />;
}

export function Select({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cx(s.control, className)} {...rest} />;
}

export function Checkbox({ label, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: ReactNode }) {
  return (
    <label className={s.check}>
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  );
}

/** Text field with label, hint and error in one line of JSX. */
export function TextField({ label, hint, error, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; hint?: ReactNode; error?: ReactNode }) {
  return (
    <Field label={label} hint={hint} error={error}>
      {(id, describedBy) => <Input id={id} aria-describedby={describedBy} invalid={Boolean(error)} {...rest} />}
    </Field>
  );
}
